/**
 * S19 · Review — pre-submit summary. Aggregates form-fill state +
 * capture counts + conflict checks so Sarah can eyeball everything in
 * one place before signing.
 */
import { useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Btn, Chip, GestureBar, I, Ico, ScreenFrame, StatusBar, T } from '@/components/primitives';
import {
  useCapturesBySession,
  useFieldValues,
  useSession,
  useSyncRollup,
} from '@kapture/client-core';
import {
  evidenceGaps,
  fieldsForSection,
  getTemplate,
  groupBySubject,
  subjectCount,
  subjectLabel,
  subjectNounOf,
  subjectPluralOf,
} from '@kapture/domain';
import { LayoutOrLeaf } from '@/components/LayoutOrLeaf';

export const Route = createFileRoute('/sessions/$sessionId/review')({
  component: ReviewPage,
  staticData: { mobile: true },
});

function ReviewPage() {
  return (
    <LayoutOrLeaf routeId="/sessions/$sessionId/review">
      <ReviewContent />
    </LayoutOrLeaf>
  );
}

function ReviewContent() {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: session } = useSession(sessionId);
  const { data: captures } = useCapturesBySession(sessionId);
  const { data: values } = useFieldValues(sessionId);
  const { data: rollup } = useSyncRollup();

  const template = getTemplate(session?.templateId);
  const filled = useMemo(() => new Set((values ?? []).map((v) => v.fieldId)), [values]);

  const totalFields = template?.fields.length ?? 0;
  const filledCount = template ? template.fields.filter((f) => filled.has(f.id)).length : 0;
  // Not `filled.has(id)`. That counted a required field as answered whenever a
  // `field_values` row existed, which is the wrong question for the three kinds
  // answered with evidence: a photos field keeps its row holding `[]` after the
  // last photo is deleted, and a signature field writes no row at all because a
  // PNG is not a form value. One of those let someone leave site believing a
  // required photograph existed; the other could never be satisfied.
  const requiredMissing = useMemo(
    () => evidenceGaps(template, values ?? [], captures ?? []),
    [template, values, captures],
  );
  const captureCount = captures?.length ?? 0;
  const conflicts = rollup?.conflict ?? 0;

  // The walk as the things it covered, not as one pile. A school fire-door
  // survey is one form and forty doors, and a reviewer reading "112 captures"
  // can tell that something was photographed but not that D-118 never was —
  // the form cannot tell them either, because `field_values` is keyed by
  // session and holds exactly one door id for the whole round.
  const { subjectGroups, subjectTotal } = useMemo(() => {
    const list = captures ?? [];
    return { subjectGroups: groupBySubject(list), subjectTotal: subjectCount(list) };
  }, [captures]);

  // What this customer calls the things they inspect, so a bridge round reads
  // "Deck panel D-114". Null when the template declares nothing, and the bare
  // code is right there — the alternative, this screen choosing a noun, would
  // print a word in front of a code that no template ever authorised.
  const subjectNoun = subjectNounOf(template);
  // The customer's own plural, or our neutral word. Never the singular noun
  // with an `s` bolted on — that prints "Gantrys" in their report.
  const subjectPlural = subjectPluralOf(template);

  // Two statuses can be submitted: work in progress, and work a reviewer sent
  // back. The second is the resubmission — without it "fix it" leads here and
  // then refuses, which is the failure this loop exists to remove. `rejected`
  // is deliberately not in the list: it is terminal, and the server's writer
  // gate would refuse the push anyway.
  const submittable = session?.status === 'active' || session?.status === 'changes_requested';
  const alreadySubmitted = !submittable;
  const canSubmit = submittable && conflicts === 0;

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <Link
          to="/sessions/$sessionId"
          params={{ sessionId }}
          aria-label="Back"
          style={{ color: T.text, display: 'flex', textDecoration: 'none' }}
        >
          <Ico d={I.back} size={20} />
        </Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Review</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            {session?.title ?? '—'}
          </div>
        </div>
        <Chip tone={alreadySubmitted ? 'good' : 'brandSoft'} size="sm">
          {session?.status ?? 'loading'}
        </Chip>
      </div>

      <div style={{ padding: 16 }}>
        <div
          style={{
            padding: 16,
            borderRadius: 16,
            background: T.ink,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Ico d={I.sparkle} size={14} fill={T.brand} stroke={T.brand} />
            <div
              style={{
                fontSize: 11,
                fontFamily: T.mono,
                color: 'rgba(255,255,255,0.6)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              {alreadySubmitted ? 'Submitted' : 'Ready to submit'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 18 }}>
            <Stat n={filledCount} total={totalFields} label="Fields" />
            <Stat n={captureCount} label="Captures" />
            <Stat n={conflicts} label="Conflicts" warn={conflicts > 0} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '0 16px 16px' }}>
        {requiredMissing.length > 0 && !alreadySubmitted && (
          <MissingRequired
            count={requiredMissing.length}
            sessionId={sessionId}
            firstFieldId={requiredMissing[0]!.fieldId}
            missingEvidence={requiredMissing.some((g) => g.reason === 'no-evidence')}
          />
        )}
        {conflicts > 0 && (
          <Link
            to="/sync/conflicts/$taskId"
            params={{ taskId: 'first' }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: 12,
              background: T.warnSoft,
              border: `1px solid ${T.warn}`,
              borderRadius: 12,
              color: 'inherit',
              textDecoration: 'none',
              marginBottom: 10,
            }}
          >
            <Ico d={I.warn} size={16} stroke={T.warnText} />
            <div style={{ flex: 1, fontSize: 12, fontFamily: T.font, color: T.warnText }}>
              <b style={{ fontWeight: 700 }}>
                {conflicts} conflict{conflicts === 1 ? '' : 's'} in sync queue
              </b>
              <div style={{ opacity: 0.85 }}>Resolve before submitting.</div>
            </div>
            <Ico d={I.chev} size={16} stroke={T.warnText} />
          </Link>
        )}

        {template && (
          <>
            <SectionHeader>Form sections</SectionHeader>
            {template.sections
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((s) => {
                const fields = fieldsForSection(template, s.id);
                const done = fields.filter((f) => filled.has(f.id)).length;
                const complete = done === fields.length;
                return (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      border: `1px solid ${T.stroke}`,
                      borderRadius: 10,
                      background: T.surface,
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 8,
                        background: complete ? T.good : T.surface2,
                        color: complete ? '#fff' : T.textDim,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {complete ? (
                        <Ico d={I.check2} size={13} sw={2.4} />
                      ) : (
                        <Ico d={I.file} size={13} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: T.font }}>
                        {s.title}
                      </div>
                      <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>
                        {done}/{fields.length}
                      </div>
                    </div>
                  </div>
                );
              })}
          </>
        )}

        <SectionHeader>Captures</SectionHeader>
        <div
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
          }}
        >
          {captureBuckets(captures ?? []).map((b) => (
            <Chip key={b.label} tone="neutral" size="sm">
              {b.label} {b.count}
            </Chip>
          ))}
        </div>

        {/* Absent until something was captured against a code, which is the
            state of every session recorded so far. `groupBySubject` still
            returns one site-level group for those, and drawing it would hang
            a heading and a single row saying "Site level 27" over a review
            that the Captures chips above have already summarised. */}
        {subjectTotal > 0 && (
          <>
            <SectionHeader>
              {subjectPlural} · {subjectTotal}
            </SectionHeader>
            {subjectGroups.map((g) => (
              <SubjectRow
                key={String(g.subjectRef)}
                subject={g.subjectRef}
                noun={subjectNoun}
                count={g.captures.length}
              />
            ))}
          </>
        )}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          icon={<Ico d={I.edit} size={14} />}
          onClick={() => navigate({ to: '/sessions/$sessionId/form', params: { sessionId } })}
        >
          Edit form
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.signature} size={16} />}
          disabled={!canSubmit}
          onClick={() =>
            navigate({ to: '/sessions/$sessionId/review/sign', params: { sessionId } })
          }
          style={{ opacity: canSubmit ? 1 : 0.5 }}
        >
          {alreadySubmitted ? 'Already submitted' : 'Sign & submit'}
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function Stat({
  n,
  total,
  label,
  warn,
}: {
  n: number;
  total?: number;
  label: string;
  warn?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          fontFamily: T.font,
          lineHeight: 1,
          color: warn ? T.warn : '#fff',
          letterSpacing: '-0.02em',
        }}
      >
        {total != null ? `${n}/${total}` : n}
      </div>
      <div
        style={{
          fontSize: 10,
          fontFamily: T.mono,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'rgba(255,255,255,0.55)',
          marginTop: 4,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function MissingRequired({
  count,
  sessionId,
  firstFieldId,
  missingEvidence,
}: {
  count: number;
  sessionId: string;
  firstFieldId: string;
  missingEvidence: boolean;
}) {
  return (
    <Link
      to="/sessions/$sessionId/form/$fieldId"
      params={{ sessionId, fieldId: firstFieldId }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 12,
        background: T.warnSoft,
        border: `1px solid ${T.warn}`,
        borderRadius: 12,
        color: 'inherit',
        textDecoration: 'none',
        marginBottom: 10,
      }}
    >
      <Ico d={I.warn} size={16} stroke={T.warnText} />
      <div style={{ flex: 1, fontSize: 12, fontFamily: T.font, color: T.warnText }}>
        <b style={{ fontWeight: 700 }}>
          {count} required {count === 1 ? 'item' : 'items'} outstanding
        </b>
        <div style={{ opacity: 0.85 }}>
          {missingEvidence
            ? // Worth saying plainly and worth saying here. A missing photo is
              // the one gap that cannot be closed from the office: the reviewer
              // sends it back and someone drives out again.
              'Some need a photo, recording or signature — capture them before you leave site.'
            : 'You can still submit, but the reviewer will flag these.'}
        </div>
      </div>
      <Ico d={I.chev} size={16} stroke={T.warnText} />
    </Link>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        color: T.textMute,
        fontFamily: T.mono,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginTop: 14,
        marginBottom: 8,
      }}
    >
      {children}
    </div>
  );
}

/**
 * One subject's share of the walk, in the order it was reached.
 *
 * Deliberately not a link and not a count of anything but captures: the code
 * is what the inspector was standing in front of, not a row in a register,
 * and a row that offered to open "D-114" would promise a thing this product
 * does not hold. Site-level evidence keeps its place in the sequence rather
 * than being sorted to the top, matching the timeline — the establishing shot
 * belongs where it was taken.
 */
function SubjectRow({
  subject,
  noun,
  count,
}: {
  subject: string | null;
  noun: string | null;
  count: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 12px',
        border: `1px solid ${T.stroke}`,
        borderRadius: 10,
        background: T.surface,
        marginBottom: 6,
      }}
    >
      <div
        style={{
          width: 24,
          height: 24,
          borderRadius: 8,
          background: subject ? T.brandSoft : T.surface2,
          color: subject ? T.brandText : T.textMute,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Ico d={subject ? I.tag : I.map} size={13} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          fontWeight: 600,
          fontFamily: T.font,
          color: subject ? T.text : T.textDim,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={subjectLabel(subject, noun)}
      >
        {subjectLabel(subject, noun)}
      </div>
      <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.mono }}>{count}</div>
    </div>
  );
}

function captureBuckets(
  captures: Array<{ kind: string }>,
): Array<{ label: string; count: number }> {
  const counts = new Map<string, number>();
  for (const c of captures) counts.set(c.kind, (counts.get(c.kind) ?? 0) + 1);
  return Array.from(counts.entries()).map(([kind, count]) => ({ label: kind, count }));
}
